import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getManualCodes, createSharedSeries, registerSeriesInventory } from '../services/apiService';

const CONTRACT_CODE_TO_LABEL = {
  '351001': 'ADM',
  '351100': 'Hard Block',
  '351101': 'Soft Block',
};

const CONTRACT_LABEL_TO_CODE = {
  ADM: '351001',
  'Hard Block': '351100',
  'Soft Block': '351101',
};

const normalizeContractCode = (val) => {
  if (val == null) return '';
  const s = String(val);
  if (CONTRACT_CODE_TO_LABEL[s]) return s;
  return CONTRACT_LABEL_TO_CODE[s] || s;
};

const isHotelSeriesType = (seriesType) => String(seriesType) === '347002';

const SharedInventoryCreateModal = ({ open, onClose, initialSeriesType = '347001', onCreated }) => {
  const [newSeriesType, setNewSeriesType] = useState(initialSeriesType);
  const [newRow, setNewRow] = useState({});
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [airportOptions, setAirportOptions] = useState([]);
  const [supplierFilter, setSupplierFilter] = useState('');
  const [departAirportFilter, setDepartAirportFilter] = useState('');
  const [arriveAirportFilter, setArriveAirportFilter] = useState('');
  const [isSupplierOpen, setIsSupplierOpen] = useState(false);
  const [departAirportOpen, setDepartAirportOpen] = useState(false);
  const [arriveAirportOpen, setArriveAirportOpen] = useState(false);
  const [enableSeatRegistration, setEnableSeatRegistration] = useState(false);
  const [newSeriesRegisterForm, setNewSeriesRegisterForm] = useState({
    startDate: '',
    endDate: '',
    seatCount: '',
    weekdays: [1, 2, 3, 4, 5, 6, 7],
  });
  const [isRegistering, setIsRegistering] = useState(false);

  const departCreateRef = useRef(null);
  const arriveCreateRef = useRef(null);
  const supplierCreateRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setNewSeriesType(initialSeriesType || '347001');
    setNewRow({ 시리즈명: '', 계약종류: '', 호텔명: '', 객실타입: '' });
    setNewSeriesRegisterForm({
      startDate: '',
      endDate: '',
      seatCount: '',
      weekdays: [1, 2, 3, 4, 5, 6, 7],
    });
    setEnableSeatRegistration(false);
    setSupplierFilter('');
    setDepartAirportFilter('');
    setArriveAirportFilter('');
    setIsSupplierOpen(false);
    setDepartAirportOpen(false);
    setArriveAirportOpen(false);
  }, [open, initialSeriesType]);

  useEffect(() => {
    if (!open) return;
    const loadSuppliers = async () => {
      try {
        const list = await getManualCodes({ kind: 'CW지급수배업체' });
        setSupplierOptions(Array.isArray(list) ? list : []);
      } catch (_) {
        setSupplierOptions([]);
      }
    };
    const loadAirports = async () => {
      try {
        const list = await getManualCodes({ kind: '공항' });
        setAirportOptions(Array.isArray(list) ? list : []);
      } catch (_) {
        setAirportOptions([]);
      }
    };
    loadSuppliers();
    loadAirports();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      const dCreate = departCreateRef.current;
      if (departAirportOpen && dCreate && !dCreate.contains(e.target)) {
        setDepartAirportOpen(false);
      }
      const aCreate = arriveCreateRef.current;
      if (arriveAirportOpen && aCreate && !aCreate.contains(e.target)) {
        setArriveAirportOpen(false);
      }
      const sCreate = supplierCreateRef.current;
      if (isSupplierOpen && sCreate && !sCreate.contains(e.target)) {
        setIsSupplierOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, departAirportOpen, arriveAirportOpen, isSupplierOpen]);

  const filteredSuppliers = useMemo(() => {
    const q = String(supplierFilter || '').trim();
    if (!q) return supplierOptions;
    return supplierOptions.filter((r) => {
      const code = String(r?.코드 ?? '');
      const name = String(r?.코드명 ?? '');
      return code.includes(q) || name.includes(q);
    });
  }, [supplierFilter, supplierOptions]);

  const filteredDepartAirports = useMemo(() => {
    const q = String(departAirportFilter || '').trim();
    if (!q) return airportOptions;
    const qLower = q.toLowerCase();
    return airportOptions.filter((r) => {
      const name = String(r?.코드명 || '');
      const code = String(r?.코드 || '');
      return (
        name.toLowerCase().includes(qLower) ||
        code.toLowerCase().includes(qLower)
      );
    });
  }, [departAirportFilter, airportOptions]);

  const filteredArriveAirports = useMemo(() => {
    const q = String(arriveAirportFilter || '').trim();
    if (!q) return airportOptions;
    const qLower = q.toLowerCase();
    return airportOptions.filter((r) => {
      const name = String(r?.코드명 || '');
      const code = String(r?.코드 || '');
      return (
        name.toLowerCase().includes(qLower) ||
        code.toLowerCase().includes(qLower)
      );
    });
  }, [arriveAirportFilter, airportOptions]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!String(newRow?.['시리즈명'] ?? '').trim()) {
      alert('시리즈명을 입력해주세요.');
      return;
    }
    if (!String(newRow?.['공급거래처번호'] ?? '').trim()) {
      alert('공급거래처를 선택해주세요.');
      return;
    }

    if (enableSeatRegistration) {
      if (!newSeriesRegisterForm.startDate || !newSeriesRegisterForm.endDate) {
        alert('좌석 등록을 위해 시작일과 종료일을 입력해주세요.');
        return;
      }
      if (!newSeriesRegisterForm.seatCount || Number(newSeriesRegisterForm.seatCount) <= 0) {
        alert('좌석 등록을 위해 좌석수를 입력해주세요.');
        return;
      }
      if (!newSeriesRegisterForm.weekdays.length) {
        alert('좌석 등록을 위해 적용요일을 선택해주세요.');
        return;
      }
    }

    try {
      setIsRegistering(true);
      const params = {
        supplierPartnerNo: newRow?.['공급거래처번호'] != null ? String(newRow['공급거래처번호']) : '',
        seriesName: String(newRow?.['시리즈명'] ?? ''),
        stockType: String(newSeriesType),
        contractType: String(normalizeContractCode(newRow?.['계약종류'] ?? '')),
        departAirportCode: String(newRow?.['출발공항'] ?? ''),
        arriveAirportCode: String(newRow?.['목적공항'] ?? ''),
        penaltyRule: '',
        color: String(newRow?.['색상'] ?? '#000000'),
        departFlightNo: String(newRow?.['출발편명'] ?? ''),
        returnFlightNo: String(newRow?.['귀국편명'] ?? ''),
        nights: newRow?.['박수'] != null ? String(newRow['박수']) : '',
        days: newRow?.['일수'] != null ? String(newRow['일수']) : '',
        oneWayType: '왕복',
        airlineExtras: '',
        airlineExtrasName: '',
        hotelName: isHotelSeriesType(newSeriesType) ? String(newRow?.['호텔명'] ?? '') : '',
        roomType: isHotelSeriesType(newSeriesType) ? String(newRow?.['객실타입'] ?? '') : '',
      };
      const result = await createSharedSeries(params);

      let createdSeriesNo = null;
      if (result?.table && Array.isArray(result.table) && result.table.length > 0) {
        createdSeriesNo = result.table[0]?.['시리즈번호'] || result.table[0]?.['상품공유번호'];
      }

      if (enableSeatRegistration) {
        if (!createdSeriesNo) {
          alert('시리즈가 추가되었습니다. 좌석 등록은 시리즈 편집에서 진행해주세요.');
        } else {
          const weekdaysStr = newSeriesRegisterForm.weekdays.slice().sort((a, b) => a - b).join(',');
          await registerSeriesInventory({
            sharedSeriesNo: createdSeriesNo,
            weekdays: weekdaysStr,
            startDate: newSeriesRegisterForm.startDate,
            endDate: newSeriesRegisterForm.endDate,
            seatCount: newSeriesRegisterForm.seatCount,
          });
          alert('시리즈와 좌석이 함께 등록되었습니다.');
        }
      } else {
        alert('시리즈가 추가되었습니다.');
      }

      if (typeof onCreated === 'function') {
        onCreated(createdSeriesNo ? { seriesNo: createdSeriesNo, params } : null);
      }
      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (e) {
      alert('시리즈 추가에 실패했습니다.');
    } finally {
      setIsRegistering(false);
    }
  };

  if (typeof document === 'undefined' || !document.body) return null;

  return createPortal(
    <div className="fixed inset-0 z-[120]">
      <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white shadow-xl z-50 flex flex-col">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-800">시리즈 추가</h2>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="p-4 overflow-auto flex-1 space-y-3">
          <div className="grid grid-cols-3 gap-3 items-center">
            <label className="text-sm text-gray-600">시리즈 종류</label>
            <select
              className="col-span-2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
              value={newSeriesType}
              onChange={(e) => setNewSeriesType(e.target.value)}
            >
              <option value="347001">항공</option>
              <option value="347002">호텔</option>
              <option value="347003">선박</option>
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3 items-center">
            <label className="text-sm text-gray-600">시리즈명</label>
            <input
              className="col-span-2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
              value={String(newRow?.['시리즈명'] ?? '')}
              onChange={(e) => setNewRow(prev => ({ ...prev, ['시리즈명']: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-3 gap-3 items-center">
            <label className="text-sm text-gray-600">계약종류</label>
            <select
              className="col-span-2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
              value={normalizeContractCode(newRow?.['계약종류'])}
              onChange={(e) => setNewRow(prev => ({ ...prev, ['계약종류']: e.target.value }))}
            >
              <option value="">선택</option>
              <option value="351001">ADM</option>
              <option value="351100">Hard Block</option>
              <option value="351101">Soft Block</option>
            </select>
          </div>

          {!isHotelSeriesType(newSeriesType) ? (
            <>
              <div className="grid grid-cols-6 gap-3 items-center">
                <label className="text-sm text-gray-600">박수</label>
                <input
                  type="number"
                  className="col-span-2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  value={String(newRow?.['박수'] ?? '')}
                  onChange={(e) => setNewRow(prev => ({ ...prev, ['박수']: e.target.value }))}
                />
                <label className="text-sm text-gray-600">일수</label>
                <input
                  type="number"
                  className="col-span-2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  value={String(newRow?.['일수'] ?? '')}
                  onChange={(e) => setNewRow(prev => ({ ...prev, ['일수']: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-6 gap-3 items-center">
                <label className="text-sm text-gray-600">가는편</label>
                <input
                  className="col-span-2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  value={String(newRow?.['출발편명'] ?? '')}
                  onChange={(e) => setNewRow(prev => ({ ...prev, ['출발편명']: e.target.value }))}
                />
                <label className="text-sm text-gray-600">오는편</label>
                <input
                  className="col-span-2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  value={String(newRow?.['귀국편명'] ?? '')}
                  onChange={(e) => setNewRow(prev => ({ ...prev, ['귀국편명']: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-6 gap-3 items-center">
                <label className="text-sm text-gray-600">출발지</label>
                <div className="col-span-2 relative" ref={departCreateRef}>
                  <button
                    type="button"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                    onClick={() => setDepartAirportOpen(v => !v)}
                  >
                    {String(newRow?.['출발공항명'] ?? '') || String(newRow?.['출발공항'] ?? '') || '선택하세요'}
                  </button>
                  {departAirportOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow">
                      <div className="p-2 border-b">
                        <input
                          autoFocus
                          type="text"
                          className="w-full border rounded px-2 py-1 text-sm"
                          placeholder="검색 (코드/명)"
                          value={departAirportFilter}
                          onChange={(e) => setDepartAirportFilter(e.target.value)}
                        />
                      </div>
                      <div className="max-h-56 overflow-auto">
                        {filteredDepartAirports.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다</div>
                        )}
                        {filteredDepartAirports.map((r) => {
                          const code = String(r?.코드 ?? '');
                          const label = String(r?.코드명 ?? '');
                          return (
                            <button
                              key={`${code}-${label}`}
                              type="button"
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${String(newRow?.['출발공항'] ?? '') === code ? 'bg-blue-50' : ''}`}
                              onClick={() => {
                                setNewRow(prev => ({ ...prev, ['출발공항']: code, ['출발공항명']: label }));
                                setDepartAirportOpen(false);
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <label className="text-sm text-gray-600">목적지</label>
                <div className="col-span-2 relative" ref={arriveCreateRef}>
                  <button
                    type="button"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                    onClick={() => setArriveAirportOpen(v => !v)}
                  >
                    {String(newRow?.['목적공항명'] ?? '') || String(newRow?.['목적공항'] ?? '') || '선택하세요'}
                  </button>
                  {arriveAirportOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow">
                      <div className="p-2 border-b">
                        <input
                          autoFocus
                          type="text"
                          className="w-full border rounded px-2 py-1 text-sm"
                          placeholder="검색 (코드/명)"
                          value={arriveAirportFilter}
                          onChange={(e) => setArriveAirportFilter(e.target.value)}
                        />
                      </div>
                      <div className="max-h-56 overflow-auto">
                        {filteredArriveAirports.length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다</div>
                        )}
                        {filteredArriveAirports.map((r) => {
                          const code = String(r?.코드 ?? '');
                          const label = String(r?.코드명 ?? '');
                          return (
                            <button
                              key={`${code}-${label}`}
                              type="button"
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${String(newRow?.['목적공항'] ?? '') === code ? 'bg-blue-50' : ''}`}
                              onClick={() => {
                                setNewRow(prev => ({ ...prev, ['목적공항']: code, ['목적공항명']: label }));
                                setArriveAirportOpen(false);
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-sm text-gray-600">호텔명</label>
                <input
                  className="col-span-2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  value={String(newRow?.['호텔명'] ?? '')}
                  onChange={(e) => setNewRow(prev => ({ ...prev, ['호텔명']: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-3 gap-3 items-center">
                <label className="text-sm text-gray-600">객실타입</label>
                <input
                  className="col-span-2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  value={String(newRow?.['객실타입'] ?? '')}
                  onChange={(e) => setNewRow(prev => ({ ...prev, ['객실타입']: e.target.value }))}
                />
              </div>
            </>
          )}

          <div className="grid grid-cols-3 gap-3 items-center">
            <label className="text-sm text-gray-600">공급거래처</label>
            <div className="col-span-2">
              <div className="relative" ref={supplierCreateRef}>
                <button
                  type="button"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                  onClick={() => setIsSupplierOpen(v => !v)}
                >
                  {String(newRow?.['공급거래처'] ?? '') || '선택하세요'}
                </button>
                {isSupplierOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow">
                    <div className="p-2 border-b">
                      <input
                        autoFocus
                        type="text"
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="검색 (코드/이름)"
                        value={supplierFilter}
                        onChange={(e) => setSupplierFilter(e.target.value)}
                      />
                    </div>
                    <div className="max-h-56 overflow-auto">
                      {filteredSuppliers.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다</div>
                      )}
                      {filteredSuppliers.map((r) => {
                        const v = String(r?.코드 ?? '');
                        const label = String(r?.코드명 ?? '');
                        return (
                          <button
                            key={v}
                            type="button"
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${String(newRow?.['공급거래처번호'] ?? '') === v ? 'bg-blue-50' : ''}`}
                            onClick={() => {
                              setNewRow(prev => ({
                                ...prev,
                                ['공급거래처번호']: v,
                                ['공급거래처']: label,
                              }));
                              setIsSupplierOpen(false);
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 items-center">
            <label className="text-sm text-gray-600">색상</label>
            <div className="col-span-2 flex items-center gap-3">
              <input
                type="color"
                className="h-9 w-12 p-1 border border-gray-300 rounded cursor-pointer"
                value={String(newRow?.['색상'] ?? '#000000')}
                onChange={(e) => setNewRow(prev => ({ ...prev, ['색상']: e.target.value }))}
              />
              <span className="text-sm text-gray-700">{String(newRow?.['색상'] ?? '#000000')}</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                id="enableSeatRegistration"
                checked={enableSeatRegistration}
                onChange={(e) => setEnableSeatRegistration(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="enableSeatRegistration" className="text-sm font-medium text-gray-700 cursor-pointer">
                시리즈 추가 시 좌석도 함께 등록
              </label>
            </div>

            {enableSeatRegistration && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="text-xs text-blue-700 mb-2">
                  💡 시리즈 생성과 동시에 출발일별 좌석을 등록합니다.
                </div>
                <div className="grid grid-cols-6 gap-3 items-center">
                  <label className="text-sm text-gray-600">시작일</label>
                  <input
                    type="date"
                    className="col-span-2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                    value={newSeriesRegisterForm.startDate}
                    onChange={(e) => setNewSeriesRegisterForm(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                  <label className="text-sm text-gray-600">종료일</label>
                  <input
                    type="date"
                    className="col-span-2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                    value={newSeriesRegisterForm.endDate}
                    onChange={(e) => setNewSeriesRegisterForm(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3 items-center">
                  <label className="text-sm text-gray-600">좌석수</label>
                  <input
                    type="number"
                    min="0"
                    className="col-span-2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                    value={newSeriesRegisterForm.seatCount}
                    onChange={(e) => setNewSeriesRegisterForm(prev => ({ ...prev, seatCount: e.target.value }))}
                    placeholder="좌석수 입력"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3 items-start">
                  <label className="text-sm text-gray-600 pt-2">적용요일</label>
                  <div className="col-span-2 space-y-2">
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: 1, label: '월' },
                        { value: 2, label: '화' },
                        { value: 3, label: '수' },
                        { value: 4, label: '목' },
                        { value: 5, label: '금' },
                        { value: 6, label: '토' },
                        { value: 7, label: '일' },
                      ].map(({ value, label }) => (
                        <label key={value} className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newSeriesRegisterForm.weekdays.includes(value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewSeriesRegisterForm(prev => ({
                                  ...prev,
                                  weekdays: [...prev.weekdays, value].slice().sort((a, b) => a - b),
                                }));
                              } else {
                                setNewSeriesRegisterForm(prev => ({
                                  ...prev,
                                  weekdays: prev.weekdays.filter(w => w !== value),
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          <span className="text-sm text-gray-700">{label}</span>
                        </label>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:text-blue-700"
                      onClick={() => {
                        const allSelected = newSeriesRegisterForm.weekdays.length === 7;
                        setNewSeriesRegisterForm(prev => ({
                          ...prev,
                          weekdays: allSelected ? [] : [1, 2, 3, 4, 5, 6, 7],
                        }));
                      }}
                    >
                      {newSeriesRegisterForm.weekdays.length === 7 ? '전체 해제' : '전체 선택'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-4 py-3 border-t flex justify-end space-x-2">
          <button className="px-4 py-2 rounded border text-gray-700 hover:bg-gray-50 text-sm" onClick={onClose}>닫기</button>
          <button
            className="px-4 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isRegistering}
            onClick={handleSubmit}
          >
            {isRegistering ? '처리 중...' : (enableSeatRegistration ? '시리즈 + 좌석 추가' : '추가')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SharedInventoryCreateModal;
